import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CtaBlock } from "../../components/CtaBlock";
import { RelatedArticles } from "../../components/RelatedArticles";

const ARTICLE = {
	title: "au PAYマーケット 商品画像サイズ 640×640 要件一覧と注意点",
	description:
		"au PAYマーケットの商品画像要件（640×640px以上、JPG/GIFのみ、2MB以内）を解説。PNG非対応という落とし穴と対処法も紹介。",
	publishDate: "2026-03-16",
	slug: "aupay-product-image-size-640",
};

const ogImageUrl = `https://og.fucina.casa/api/og?title=${encodeURIComponent(ARTICLE.title)}&description=${encodeURIComponent(ARTICLE.description)}&locale=ja`;

export default function AupayProductImageSize640() {
	return (
		<>
			<Helmet>
				<title>{ARTICLE.title} | しろぬきや</title>
				<meta name="description" content={ARTICLE.description} />
				<meta property="og:title" content={ARTICLE.title} />
				<meta property="og:description" content={ARTICLE.description} />
				<meta
					property="og:url"
					content={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<meta property="og:type" content="article" />
				<meta property="og:image" content={ogImageUrl} />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={ARTICLE.title} />
				<meta name="twitter:description" content={ARTICLE.description} />
				<meta name="twitter:image" content={ogImageUrl} />
				<link
					rel="canonical"
					href={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "Article",
						headline: ARTICLE.title,
						description: ARTICLE.description,
						datePublished: ARTICLE.publishDate,
						publisher: {
							"@type": "Organization",
							name: "しろぬきや",
							url: "https://shironukiya.fucina.casa",
						},
					})}
				</script>
			</Helmet>

			<div className="max-w-3xl mx-auto px-4 py-10">
				<nav className="text-sm text-gray-500 mb-6">
					<Link to="/" className="hover:underline">
						ホーム
					</Link>
					{" > "}
					<Link to="/blog" className="hover:underline">
						ブログ
					</Link>
					{" > "}
					<span>{ARTICLE.title}</span>
				</nav>

				<article>
					<h1 className="text-2xl font-bold mb-4">{ARTICLE.title}</h1>
					<p className="text-gray-500 text-sm mb-8">
						更新日: {ARTICLE.publishDate}
					</p>

					<p className="text-gray-700 mb-6">
						au
						PAYマーケット（旧Wowma!）に出品しようとして、画像のアップロードでつまずいたことはありませんか？
						au PAYマーケットには<strong>PNGが使えない</strong>
						など、他のモールと異なる独自の制約があります。
						知らずにPNGをアップロードしようとしてエラーになるケースが非常に多いため、要件を事前に把握しておきましょう。
					</p>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							au PAYマーケットの商品画像要件一覧
						</h2>
						<div className="overflow-x-auto mb-4">
							<table className="w-full text-sm border-collapse border border-gray-200">
								<thead>
									<tr className="bg-gray-50">
										<th className="border border-gray-200 px-4 py-2 text-left">
											項目
										</th>
										<th className="border border-gray-200 px-4 py-2 text-left">
											要件
										</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td className="border border-gray-200 px-4 py-2">
											推奨最小サイズ
										</td>
										<td className="border border-gray-200 px-4 py-2">
											<strong>640×640px 以上</strong>
										</td>
									</tr>
									<tr className="bg-gray-50">
										<td className="border border-gray-200 px-4 py-2">
											ファイル形式
										</td>
										<td className="border border-gray-200 px-4 py-2">
											<strong>JPG（.jpg）、GIF（.gif）のみ</strong>
											<span className="text-red-600 ml-2">※PNG不可</span>
										</td>
									</tr>
									<tr>
										<td className="border border-gray-200 px-4 py-2">
											最大ファイルサイズ
										</td>
										<td className="border border-gray-200 px-4 py-2">
											<strong>2MB 以内</strong>
										</td>
									</tr>
									<tr className="bg-gray-50">
										<td className="border border-gray-200 px-4 py-2">
											最大登録枚数
										</td>
										<td className="border border-gray-200 px-4 py-2">20枚</td>
									</tr>
								</tbody>
							</table>
						</div>
						<div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
							<p className="text-sm text-red-800">
								<strong>⚠️ 重要</strong>：au PAYマーケットは
								<strong>PNG形式に対応していません</strong>。
								背景除去した透明PNGやWebP形式をそのまま使おうとするとエラーになります。必ずJPG形式に変換してからアップロードしてください。
							</p>
						</div>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							よくあるエラーと原因
						</h2>
						<div className="space-y-4">
							<div className="border rounded-lg p-4">
								<p className="font-semibold text-gray-800 mb-1">
									「対応していないファイル形式です」
								</p>
								<p className="text-sm text-gray-700">
									→
									PNG・WebP・BMP等の非対応形式を使っている場合。JPG（.jpg）に変換してください。
								</p>
							</div>
							<div className="border rounded-lg p-4">
								<p className="font-semibold text-gray-800 mb-1">
									「ファイルサイズが2MBを超えています」
								</p>
								<p className="text-sm text-gray-700">
									→
									高解像度JPGのままアップロードしようとした場合。画像圧縮ツールを使い、2MB以内に収めてください。640×640pxにリサイズするとほとんどの場合2MB以内に収まります。
								</p>
							</div>
							<div className="border rounded-lg p-4">
								<p className="font-semibold text-gray-800 mb-1">
									「画像サイズが小さすぎます」
								</p>
								<p className="text-sm text-gray-700">
									→
									640×640px未満の画像を使っている場合。640×640px以上になるよう元の写真から作り直してください。
								</p>
							</div>
						</div>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							PNGをJPGに変換する手順
						</h2>
						<p className="text-gray-700 mb-4">
							背景除去後のPNG画像（透明背景）をau
							PAYマーケットに対応したJPG形式に変換する場合、
							透明部分を白色に置き換えてからJPG保存する必要があります。
						</p>
						<ol className="list-decimal list-inside space-y-3 text-gray-700">
							<li>
								<strong>背景除去ツールで背景を取り除く</strong>
								<br />
								<span className="text-sm ml-5 block mt-1">
									透明PNGの状態で保存します。
								</span>
							</li>
							<li>
								<strong>透明部分を白色に変換する</strong>
								<br />
								<span className="text-sm ml-5 block mt-1">
									そのままJPGに変換すると透明部分が黒になってしまいます。必ず白背景を合成してからJPG保存してください。
								</span>
							</li>
							<li>
								<strong>640×640px以上にリサイズしてJPG保存する</strong>
								<br />
								<span className="text-sm ml-5 block mt-1">
									JPEG品質は80〜85%程度で、ファイルサイズが2MB以内になるよう調整します。
								</span>
							</li>
						</ol>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							他ECモールとの要件比較
						</h2>
						<p className="text-gray-700 mb-4">
							複数のモールに出品している場合、各モールの要件の違いに注意が必要です。
						</p>
						<div className="overflow-x-auto mb-4">
							<table className="w-full text-sm border-collapse border border-gray-200">
								<thead>
									<tr className="bg-gray-50">
										<th className="border border-gray-200 px-4 py-2 text-left">
											モール
										</th>
										<th className="border border-gray-200 px-4 py-2 text-left">
											推奨サイズ
										</th>
										<th className="border border-gray-200 px-4 py-2 text-left">
											PNG可否
										</th>
										<th className="border border-gray-200 px-4 py-2 text-left">
											容量上限
										</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td className="border border-gray-200 px-4 py-2">au PAY</td>
										<td className="border border-gray-200 px-4 py-2">
											640×640px+
										</td>
										<td className="border border-gray-200 px-4 py-2 text-red-600">
											❌ 不可
										</td>
										<td className="border border-gray-200 px-4 py-2">2MB</td>
									</tr>
									<tr className="bg-gray-50">
										<td className="border border-gray-200 px-4 py-2">Amazon</td>
										<td className="border border-gray-200 px-4 py-2">
											1600×1600px推奨
										</td>
										<td className="border border-gray-200 px-4 py-2 text-green-600">
											✅ 可
										</td>
										<td className="border border-gray-200 px-4 py-2">
											上限なし
										</td>
									</tr>
									<tr>
										<td className="border border-gray-200 px-4 py-2">
											楽天市場
										</td>
										<td className="border border-gray-200 px-4 py-2">
											700×700px+
										</td>
										<td className="border border-gray-200 px-4 py-2 text-green-600">
											✅ 可
										</td>
										<td className="border border-gray-200 px-4 py-2">5MB</td>
									</tr>
									<tr className="bg-gray-50">
										<td className="border border-gray-200 px-4 py-2">
											メルカリ
										</td>
										<td className="border border-gray-200 px-4 py-2">
											1080×1080px推奨
										</td>
										<td className="border border-gray-200 px-4 py-2 text-green-600">
											✅ 可
										</td>
										<td className="border border-gray-200 px-4 py-2">
											上限なし
										</td>
									</tr>
									<tr>
										<td className="border border-gray-200 px-4 py-2">Qoo10</td>
										<td className="border border-gray-200 px-4 py-2">
											800×800px推奨
										</td>
										<td className="border border-gray-200 px-4 py-2 text-green-600">
											✅ 可
										</td>
										<td className="border border-gray-200 px-4 py-2">5MB</td>
									</tr>
								</tbody>
							</table>
						</div>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							しろぬきやで解決する方法
						</h2>
						<p className="text-gray-700 mb-4">
							au
							PAYマーケット向けのJPG変換（透明→白背景）＋640×640pxリサイズ＋2MB以内への圧縮を、
							毎回手作業でやるのは大変です。
							<strong>しろぬきや</strong>
							を使えば、AI背景除去＋各モール規格への自動リサイズがブラウザで完結します。
							JPG形式での出力にも対応しているため、au
							PAYマーケットにそのまま使えます。
							画像はサーバーに送信しないためプライバシーも安全。無料で1日5枚まで利用できます。
						</p>
						<CtaBlock />
					</section>

					<RelatedArticles current={ARTICLE.slug} />
				</article>
			</div>
		</>
	);
}
